
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import {
  canPasteElement,
  canPasteElementProperties,
  copyElement,
  getElementToPasteAndPasteElement,
  pasteElementProperties
} from "@/lib/copy-paste/copy-paste";
import RightClickMenuItem from "./RightClickMenuItem.vue";
import { documentStore, uiStore } from "@/store/store";
import { getRightClickMenuPositionInCanvas } from "@/lib/right-click/actions";
import { canSaveAsGlobalBlock } from "@/lib/global-blocks/global-blocks";
import { getNodeIdAtPointInCanvas } from "@/lib/canvas/drag/element-drag";
import GlobalBlockDialog from "@/components/Reusable/GlobalBlockDialog.vue";
import ClipboardDialog from "@/components/Canvas/OverlaidUI/Canvas/ClipboardDialog.vue";
import { getClipboardStatus } from "@/lib/copy-paste/utils";
import PresetDialog from "@/components/Settings/ElementPresets/PresetDialog.vue";
import { doesElementHavePreset } from "@/lib/presets/presets";

@Component({
  components: {
    PresetDialog,
    ClipboardDialog,
    GlobalBlockDialog,
    RightClickMenuItem
  }
})
export default class RightClickMenu extends Vue {
  clipboardDialog = false;

  async mounted() {
    window.Breakdance.canvas.document.addEventListener(
      "contextmenu",
      this.handleRightClickInCanvas
    );

    window.Breakdance.canvas.document.addEventListener(
      "click",
      this.closeRightClickMenuIfClickHappensOutsideRightClickMenu,
      {
        capture: true
      }
    );
    window.addEventListener(
      "click",
      this.closeRightClickMenuIfClickHappensOutsideRightClickMenu,
      {
        capture: true
      }
    );
  }

  beforeDestroy() {
    window.Breakdance.canvas.document.removeEventListener(
      "contextmenu",
      this.handleRightClickInCanvas
    );

    window.Breakdance.canvas.document.removeEventListener(
      "click",
      this.closeRightClickMenuIfClickHappensOutsideRightClickMenu,
      {
        capture: true
      }
    );
    window.removeEventListener(
      "click",
      this.closeRightClickMenuIfClickHappensOutsideRightClickMenu,
      {
        capture: true
      }
    );
  }

  get menuState() {
    return uiStore.onCanvasRightClickMenuState !== "closed"
      ? uiStore.onCanvasRightClickMenuState
      : null;
  }

  get shouldShow() {
    return (
      uiStore.onCanvasRightClickMenuState !== "closed" &&
      !this.saveGlobalBlockDialog
    );
  }

  handleRightClickInCanvas(e: MouseEvent) {
    e.preventDefault();

    const elementId = getNodeIdAtPointInCanvas(e.clientX, e.clientY);
    const position = getRightClickMenuPositionInCanvas(e.clientX, e.clientY);

    if (!position) return;

    uiStore.setOnCanvasRightClickMenuState({
      elementId,
      position
    });
  }

  closeRightClickMenuIfClickHappensOutsideRightClickMenu(e: MouseEvent) {
    if (this.saveGlobalBlockDialog) return;

    const closestMenu = (e.target as HTMLElement).closest(
      ".breakdance-canvas-right-click-menu"
    );

    if (!closestMenu) {
      uiStore.setOnCanvasRightClickMenuState("closed");
    }
  }

  get wrapperPositionStyle() {
    if (!this.menuState) return {};

    return {
      left: `${this.menuState.position.x}px`,
      top: `${this.menuState.position.y}px`
    };
  }

  get elementIdPresent() {
    return this.menuState?.elementId;
  }

  canPasteElement = false;
  canPasteElementProperties = false;
  permission?: PermissionState;

  @Watch("menuState")
  async onStateChange() {
    const { permission } = await getClipboardStatus();

    this.permission = permission?.state;

    if (this.permission === "prompt") {
      this.canPasteElement = true;
      this.canPasteElementProperties = true;
      return;
    }

    this.canPasteElement = await canPasteElement(
      this.menuState?.elementId
        ? this.menuState.elementId
        : documentStore.document.tree.root.id
    );

    this.canPasteElementProperties = this.menuState?.elementId
      ? await canPasteElementProperties(this.menuState.elementId)
      : false;
  }

  copyElement() {
    if (this.menuState?.elementId) {
      copyElement(this.menuState.elementId);
    }
    uiStore.setOnCanvasRightClickMenuState("closed");
  }

  pasteElement() {
    if (this.permission === "prompt") {
      this.clipboardDialog = true;
    }

    if (this.menuState?.elementId) {
      getElementToPasteAndPasteElement(this.menuState.elementId);
    } else {
      getElementToPasteAndPasteElement(documentStore.document.tree.root.id);
    }

    uiStore.setOnCanvasRightClickMenuState("closed");
  }

  pasteElementProperties(path: "design" | "content" | "settings") {
    if (this.permission === "prompt") {
      this.clipboardDialog = true;
    }

    if (this.menuState?.elementId) {
      pasteElementProperties(this.menuState.elementId, path);
    }

    uiStore.setOnCanvasRightClickMenuState("closed");
  }

  deleteElement() {
    if (!this.menuState?.elementId) return;

    documentStore.deleteElement({ nodeId: this.menuState?.elementId });
    uiStore.setOnCanvasRightClickMenuState("closed");
  }

  // Global Block

  elementIdToSaveAsGlobalBlock: number | null = null;
  saveGlobalBlockDialog = false;
  openSaveGlobalBlockDialog() {
    if (this.menuState?.elementId) {
      this.elementIdToSaveAsGlobalBlock = this.menuState.elementId;
      this.saveGlobalBlockDialog = true;
    }
  }

  closeGlobalBlockDialog() {
    this.saveGlobalBlockDialog = false;
    this.elementIdToSaveAsGlobalBlock = null;
    uiStore.setOnCanvasRightClickMenuState("closed");
  }

  get canSaveAsGlobalBlock() {
    return (
      this.menuState?.elementId &&
      canSaveAsGlobalBlock(this.menuState.elementId)
    );
  }

  // Preset

  elementIdToSaveAsPreset: number | null = null;
  savePresetDialog = false;
  openSavePresetDialog() {
    console.log();
    if (this.menuState?.elementId) {
      this.elementIdToSaveAsPreset = this.menuState.elementId;
      this.savePresetDialog = true;
    }
  }

  closePresetDialog() {
    this.savePresetDialog = false;
    this.elementIdToSaveAsPreset = null;
    uiStore.setOnCanvasRightClickMenuState("closed");
  }

  get canSaveAsPreset() {
    return !doesElementHavePreset(this.menuState?.elementId);
  }
}
